/**
 * Inicialización de Firebase (Web SDK v11 modular) para el panel admin.
 *
 * Aísla los servicios Firebase y cablea los adaptadores de `@ibercar/rider-core`
 * (mismo proyecto, mismas flags Remote Config que rider). Solo lo importa el
 * `firebaseAuthSource` real y el bootstrap de `main.tsx`; los tests usan fakes y
 * NO tocan este módulo (regla del core hexagonal de rider-core).
 */
import {
  RemoteConfigFlags,
  activateAppCheckWeb,
  bootstrapRemoteConfig,
  createFirebaseIdTokenProvider,
} from '@ibercar/rider-core/firebase';
import { REMOTE_CONFIG_FETCH_TIMEOUT_MS, REMOTE_CONFIG_MIN_FETCH_MS, StaticFlags, type RemoteFlags } from '@ibercar/rider-core';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import { connectDatabaseEmulator, getDatabase, type Database } from 'firebase/database';
import { getRemoteConfig, type RemoteConfig } from 'firebase/remote-config';
import { adminConfig } from '../config/env';

export interface FirebaseServices {
  app: FirebaseApp;
  auth: Auth;
  db: Database;
  remoteConfig: RemoteConfig | null;
  /** Lectura de feature flags (mismas que rider). En F0 aún no alimenta UI. */
  flags: RemoteFlags;
  /** idToken Firebase actual, para el canje a JWT NestJS (NestAuthSession). */
  idTokenProvider: ReturnType<typeof createFirebaseIdTokenProvider>;
}

let services: FirebaseServices | null = null;

/** Inicializa Firebase una sola vez y devuelve los servicios cableados. */
export function getFirebase(): FirebaseServices {
  if (services) return services;

  const app = initializeApp(adminConfig.firebase);
  const auth = getAuth(app);
  const db = getDatabase(app, adminConfig.firebase.databaseURL);
  // Remote Config no tiene emulador: QA usa los defaults locales y no inicia
  // Firebase Installations ni consulta políticas del proyecto remoto.
  const remoteConfig = adminConfig.useFirebaseEmulators ? null : getRemoteConfig(app);

  if (adminConfig.useFirebaseEmulators) {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectDatabaseEmulator(db, '127.0.0.1', 9000);
  } else if (adminConfig.recaptchaSiteKey) {
    activateAppCheckWeb(app, adminConfig.recaptchaSiteKey);
  }

  // Remote Config: defaults locales + activación no-fatal (paridad con Flutter).
  const minFetch =
    adminConfig.environment === 'prod'
      ? REMOTE_CONFIG_MIN_FETCH_MS.prod
      : REMOTE_CONFIG_MIN_FETCH_MS.devOrStaging;
  if (remoteConfig) {
    void bootstrapRemoteConfig(remoteConfig, {
      minimumFetchIntervalMillis: minFetch,
      fetchTimeoutMillis: REMOTE_CONFIG_FETCH_TIMEOUT_MS,
    });
  }

  services = {
    app,
    auth,
    db,
    remoteConfig,
    flags: remoteConfig ? new RemoteConfigFlags(remoteConfig) : new StaticFlags(),
    idTokenProvider: createFirebaseIdTokenProvider(auth),
  };
  return services;
}
